import React, { Fragment, useState } from "react";
import { styled } from "@mui/system";
import { useRouter } from "next/router";
import {
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Box,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretDown,
  faCaretUp,
  faPlus,
  faMinus,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import { baseURL } from "@/services/http";
const Root = styled("div")({
  root: {
    width: "100%",
    maxWidth: 360,
  },
});

export const NestedMenu = ({
  menuItems,
  usePlusMinus,
  colorPalette,
  toggleNavMenu,
  initialOpenSubMenus,
  selectedItem,
}) => {
  const [openSubMenus, setOpenSubMenus] = useState(initialOpenSubMenus || []);
  const [openDialog, setOpenDialog] = useState(null);
  const [openWarningDialog, setOpenWarningDialog] = useState(null);
  const router = useRouter();
  const currentPath = router.asPath;

  const isSelected = (navPath) => {
    return navPath === currentPath;
  };

  const handleSubMenuClick = (event, pathIndices, menuItem) => {
    event.stopPropagation();
    setOpenSubMenus((prevOpenSubMenus) => {
      const isSubMenuOpen = prevOpenSubMenus.some(
        (indices) =>
          indices.length === pathIndices.length &&
          indices.every((value, index) => value === pathIndices[index])
      );
      if (isSubMenuOpen) {
        return prevOpenSubMenus.filter(
          (indices) =>
            indices.length !== pathIndices.length ||
            !indices.every((value, index) => value === pathIndices[index])
        );
      } else {
        return [...prevOpenSubMenus, pathIndices];
      }
    });
  };

  const handleNavigation = (event, menuItem) => {
    event.preventDefault();
    const { navPath, alertTitle, alertText } = menuItem;
    // Check if there is an alert that must be shown before the user navigates to the page. The navigation
    // will happen when the user clicks proceed
    if (alertTitle || alertText) {
      setOpenWarningDialog(menuItem);
      return;
    }
    // Check if the current route is /applications and do nothing if navPath matches it
    if (
      !navPath ||
      (router.pathname === "/applications" && navPath === "/applications")
    ) {
      return;
    }
    handleRedirect(navPath);
  };

  const handleRedirect = (navPath) => {
    if (navPath.includes(baseURL)) {
      toggleNavMenu && toggleNavMenu(false)();
      window.open(navPath, "_blank");
      return;
    }
    if (navPath) {
      toggleNavMenu && toggleNavMenu(false)();
      router.push(navPath);
      return;
    }
  };

  const renderSubMenuItems = (
    subItems,
    level,
    parentIndices = [],
    menuItem
  ) => {
    return subItems.map((subItem, subIndex) => {
      const currentIndices = [...parentIndices, subIndex];
      const getDataTestId = (prefix) => {
        return `${prefix}-${currentIndices.join("-")}`;
      };

      if (subItem.subItems) {
        const isSubMenuSelected = subItem.subItems.some((subItem) =>
          isSelected(subItem.navPath)
        );
        const isSubMenuOpen =
          openSubMenus.some(
            (indices) =>
              indices.length === currentIndices.length &&
              indices.every((value, index) => value === currentIndices[index])
          ) || isSubMenuSelected;

        return (
          <React.Fragment key={subIndex}>
            <ListItemButton
              sx={{
                pl: menuItem.subMenuOnly ? 2 * level : 4 * level,
                "&.Mui-focusVisible": {
                  outline: `2px solid ${colorPalette?.iconColor || "currentColor"}`,
                  outlineOffset: "-2px",
                },
              }}
              data-testid={getDataTestId("menuItem")}
              disabled={subItem.disabled}
              onClick={(e) => {
                if (subItem.expandOnClick) {
                  handleSubMenuClick(e, currentIndices);
                  if (subItem.navPath) {
                    handleNavigation(e, subItem);
                  }
                } else {
                  handleNavigation(e, subItem);
                }
              }}
            >
              {subItem.icon && (
                <ListItemIcon
                  sx={{
                    color: colorPalette?.subItemIconColor || "inherit",
                  }}
                >
                  {subItem.icon}
                </ListItemIcon>
              )}
              {subItem.text && (
                <ListItemText
                  primary={subItem.text}
                  sx={{
                    color: colorPalette?.iconColor || "inherit",
                  }}
                />
              )}
              {subItem.additionalInfo && (
                <Tooltip
                  title={"Additional Info"}
                  enterTouchDelay={0}
                  leaveTouchDelay={5000}
                >
                  <IconButton
                    size="small"
                    onClick={() => setOpenDialog(subItem)}
                  >
                    <FontAwesomeIcon icon={faCircleInfo} />
                  </IconButton>
                </Tooltip>
              )}
              <Box
                data-testid={getDataTestId("expandCollapseIcon")}
                onClick={(e) => handleSubMenuClick(e, currentIndices)}
                sx={{
                  color: colorPalette?.iconColor || "inherit",
                  ml: 2,
                }}
              >
                {isSubMenuOpen ? (
                  <FontAwesomeIcon
                    icon={faMinus}
                    onClick={(e) => {
                      handleSubMenuClick(e, currentIndices);
                      handleNavigation(e, menuItem);
                    }}
                  />
                ) : (
                  <FontAwesomeIcon
                    onClick={(e) => {
                      handleSubMenuClick(e, currentIndices);
                      handleNavigation(e, menuItem);
                    }}
                    icon={faPlus}
                  />
                )}
              </Box>
            </ListItemButton>
            {subItem.disabledText && (
              <Tooltip
                title={subItem.disabledText}
                enterTouchDelay={0}
                leaveTouchDelay={5000}
              >
                <IconButton size="small">
                  <FontAwesomeIcon icon={faCircleInfo} />
                </IconButton>
              </Tooltip>
            )}
            {subItem.component && (
              <Box
                sx={{
                  color: colorPalette?.subItemIconColor || "inherit",
                  ml: 3,
                  mt: 1,
                  mb: 1,
                }}
              >
                {subItem.component}
              </Box>
            )}
            <Collapse in={isSubMenuOpen} timeout="auto" unmountOnExit>
              {renderSubMenuItems(
                subItem.subItems,
                2,
                currentIndices,
                menuItem
              )}
            </Collapse>
          </React.Fragment>
        );
      }

      return (
        <Fragment key={subIndex}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor:
                isSelected(subItem.navPath) || selectedItem == subItem["text"]
                  ? colorPalette?.selectedColor
                  : "inherit",
            }}
          >
            <ListItemButton
              sx={{
                pl: menuItem.subMenuOnly ? 2 * level : 4 * level,
                opacity: subItem.infoOnDisabled && subItem.disabled ? 0.4 : 1,
                "&.Mui-focusVisible": {
                  outline: `2px solid ${colorPalette?.iconColor || "currentColor"}`,
                  outlineOffset: "-2px",
                },
              }}
              data-testid={getDataTestId("menuItem")}
              disabled={subItem.disabled && !subItem.infoOnDisabled}
              onClick={(e) => {
                if (subItem.disabled && subItem.infoOnDisabled) {
                  setOpenDialog(subItem);
                } else {
                  subItem.onClick && subItem.onClick(e);
                  handleNavigation(e, subItem);
                }
              }}
            >
              {subItem.icon && (
                <ListItemIcon
                  sx={{
                    color: colorPalette?.subItemIconColor || "inherit",
                  }}
                >
                  {subItem.icon}
                </ListItemIcon>
              )}
              {subItem.text && (
                <ListItemText
                  primary={subItem.text}
                  sx={{
                    color: colorPalette?.iconColor || "inherit",
                  }}
                />
              )}
            </ListItemButton>
            {subItem.disabledText && (
              <Tooltip
                title={subItem.disabledText}
                sx={{ pr: 2 }}
                enterTouchDelay={0}
                leaveTouchDelay={5000}
              >
                <IconButton size="small">
                  <FontAwesomeIcon icon={faCircleInfo} />
                </IconButton>
              </Tooltip>
            )}
            {subItem.additionalInfo && !subItem.disabledText && (
              <Tooltip
                title={"Additional Info"}
                enterTouchDelay={0}
                leaveTouchDelay={5000}
              >
                <IconButton size="small" onClick={() => setOpenDialog(subItem)}>
                  <FontAwesomeIcon icon={faCircleInfo} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {subItem.component && (
            <Box
              sx={{
                color: colorPalette?.subItemIconColor || "inherit",
                ml: 3,
                mt: 0,
                mb: 0,
              }}
            >
              {subItem.component}
            </Box>
          )}
        </Fragment>
      );
    });
  };

  const renderMenuItems = () => {
    return menuItems.map((menuItem, index) => {
      const noText = !menuItem.text;
      // Make the outer menu item be open on the leftnav if the current path is a subpath of the outer menu item.
      // Figure out a way to make this generic later.
      const outerMenuOptions = [
        {
          path: "/reports",
          text: "Reports",
        },
        {
          path: "/organizationDashboard",
          text: "Organizations",
        },
        {
          path: "/eventManagement",
          text: "Organizations",
        },
      ];
      const isSubMenuOpen =
        openSubMenus.some(
          (indices) => indices.length === 1 && indices[0] === index
        ) ||
        outerMenuOptions.some(
          (option) =>
            currentPath.includes(option.path) && option.text === menuItem.text
        );
      return (
        <React.Fragment key={index}>
          <Box
            sx={{
              display: "flex",
              justifyContent: noText ? "center" : "flex-start",
              pl: 1,
              backgroundColor: isSelected(menuItem.navPath)
                ? colorPalette?.selectedColor
                : "inherit",
            }}
          >
            {!menuItem.subMenuOnly && (
              <ListItemButton
                data-testid={`menuItem-${index}`}
                disabled={menuItem.disabled}
                onClick={(e) => {
                  if (menuItem.expandOnClick) {
                    handleSubMenuClick(e, [index]);
                  }
                  if (!menuItem.customNavHandler) {
                    handleNavigation(e, menuItem);
                  } else {
                    menuItem.customNavHandler(e);
                  }
                }}
                sx={{
                  justifyContent: noText ? "center" : "flex-start",
                  marginBottom: noText ? 1 : 0,
                  "&.Mui-focusVisible": {
                    outline: `2px solid ${colorPalette?.iconColor || "currentColor"}`,
                    outlineOffset: "-2px",
                  },
                }}
              >
                {menuItem.icon && (
                  <ListItemIcon
                    sx={{
                      color: colorPalette?.iconColor || "inherit",
                    }}
                  >
                    {menuItem.icon}
                  </ListItemIcon>
                )}
                {menuItem.text && (
                  <ListItemText
                    sx={{
                      color: colorPalette?.iconColor || "inherit",
                    }}
                    primary={menuItem.text}
                  />
                )}
              </ListItemButton>
            )}
            {menuItem.subItems && !menuItem.subMenuOnly && (
              <Box
                data-testid={`expandCollapseIcon-${index}`}
                onClick={(e) => handleSubMenuClick(e, [index])}
                sx={{ mr: 2, mt: 1.25, cursor: "pointer" }}
              >
                {usePlusMinus ? (
                  isSubMenuOpen ? (
                    <FontAwesomeIcon
                      color={colorPalette?.iconColor || "inherit"}
                      icon={faMinus}
                    />
                  ) : (
                    <FontAwesomeIcon
                      color={colorPalette?.iconColor || "inherit"}
                      icon={faPlus}
                    />
                  )
                ) : isSubMenuOpen ? (
                  <FontAwesomeIcon
                    color={colorPalette?.iconColor || "inherit"}
                    icon={faCaretUp}
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faCaretDown}
                    color={colorPalette?.iconColor || "inherit"}
                  />
                )}
              </Box>
            )}
          </Box>
          {menuItem.subItems && (
            <Box
              sx={{
                display: "flex",
                justifyContent: noText ? "center" : "flex-start",
                backgroundColor:
                  colorPalette?.subItemBackgroundColor || "inherit",
              }}
            >
              <Collapse
                in={isSubMenuOpen || menuItem.subMenuOnly}
                timeout="auto"
                unmountOnExit
              >
                {renderSubMenuItems(menuItem.subItems, 1, [index], menuItem)}
              </Collapse>
            </Box>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <Root>
      <List sx={{ pt: 0 }} component="nav">
        {renderMenuItems()}
      </List>
      <Dialog
        open={!!openDialog}
        onClose={() => setOpenDialog(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {openDialog && openDialog.text}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {openDialog && openDialog.additionalInfo}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setOpenDialog(null)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!openWarningDialog}
        onClose={() => setOpenWarningDialog(null)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {openWarningDialog && openWarningDialog.alertTitle}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {openWarningDialog && openWarningDialog.alertText}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenWarningDialog(null)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setOpenWarningDialog(null);
              handleRedirect(openWarningDialog.navPath);
            }}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
};
